import { Camera } from 'lucide-react'
import type { WireArticleFull } from '../../../api/types'
import { useArticleArtifacts } from '../common/ArticleArtifactsContext'
import { PortionMark } from '../common/PortionMark'

interface ArticleThumbnailProps {
  article: WireArticleFull
}

/**
 * Full-bleed article thumbnail.
 * Width is controlled by the parent layout — ArticleRenderer places this in the
 * 'full' layout zone so it expands to the container edge, wider than the body column.
 */
export function ArticleThumbnail({ article }: ArticleThumbnailProps) {
  const artifacts = useArticleArtifacts()
  if (!article.thumbnail) return null

  const { thumbnail } = article
  const imgSrc = artifacts.find(a => a.id === thumbnail.artifactId)?.presignedUrl
    ?? thumbnail.presignedUrl
    ?? ''

  if (!imgSrc) return null

  return (
    <figure className="m-0">
      <img
        src={imgSrc}
        alt={thumbnail.altText ?? article.title}
        className="w-full rounded object-cover"
        style={{
          maxHeight: '480px',
          ...(thumbnail.focalPoint
            ? { objectPosition: `${thumbnail.focalPoint.x * 100}% ${thumbnail.focalPoint.y * 100}%` }
            : {}),
        }}
      />
      {(thumbnail.classification || thumbnail.caption || thumbnail.credit) && (
        <figcaption className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-fg-secondary">
          <Camera size={10} aria-hidden="true" />
          <PortionMark classification={thumbnail.classification} disseminationControls={thumbnail.disseminationControls} />
          {thumbnail.credit && <><span aria-hidden="true">·</span><span className="italic opacity-70">{thumbnail.credit}</span></>}
          {thumbnail.caption && <><span aria-hidden="true">·</span><PortionMark classification={thumbnail.captionClassification} disseminationControls={thumbnail.captionDisseminationControls} />{thumbnail.caption}</>}
        </figcaption>
      )}
    </figure>
  )
}
